/**
 * IMAP protocol parser and command builder.
 * Pure string logic — no I/O, no Node.js dependencies.
 * Works in browser, Node.js, or worker thread.
 */

/** Parsed IMAP response line */
export interface ImapResponse {
    /** Tag ("*" for untagged, "+" for continuation, or the command tag) */
    tag: string;
    /** Status (OK, NO, BAD, BYE, PREAUTH) or response type (EXISTS, RECENT, FETCH, etc.) */
    type: string;
    /** The full text after the tag and type */
    text: string;
    /** Raw line */
    raw: string;
    /** Literal data keyed by BODY section name (e.g. "BODY[]", "BODY[HEADER]") */
    literals?: Map<string, string>;
}

/** Parsed FETCH response data */
export interface FetchData {
    seq: number;
    uid?: number;
    flags?: Set<string>;
    internalDate?: Date;
    size?: number;
    envelope?: EnvelopeData;
    bodyStructure?: any;
    headers?: string;
    source?: string;
}

/** Parsed ENVELOPE data */
export interface EnvelopeData {
    date: Date | null;
    subject: string;
    from: AddressData[];
    sender: AddressData[];
    replyTo: AddressData[];
    to: AddressData[];
    cc: AddressData[];
    bcc: AddressData[];
    inReplyTo: string;
    messageId: string;
}

export interface AddressData {
    name: string;
    address: string;
}

/** Parsed folder LIST data */
export interface ListData {
    flags: string[];
    delimiter: string;
    path: string;
}

/** Parsed STATUS data */
export interface StatusData {
    messages?: number;
    recent?: number;
    uidNext?: number;
    uidValidity?: number;
    unseen?: number;
    /** Total mailbox size in octets — RFC 8438 `STATUS (SIZE)`, only when the
     *  server advertises STATUS=SIZE and the caller asked for it. */
    size?: number;
}

// ── Command Builder ──

let tagCounter = 0;

/** Generate a unique command tag */
export function nextTag(): string {
    return `A${++tagCounter}`;
}

/** Reset tag counter (for testing) */
export function resetTags(): void {
    tagCounter = 0;
}

/** Build an IMAP command string (tag + command + CRLF) */
export function buildCommand(tag: string, command: string): string {
    return `${tag} ${command}\r\n`;
}

/** Build LOGIN command */
export function loginCommand(tag: string, user: string, pass: string): string {
    // Quote user and password to handle special characters
    return buildCommand(tag, `LOGIN ${quoteString(user)} ${quoteString(pass)}`);
}

/** Build AUTHENTICATE XOAUTH2 command */
export function xoauth2Command(tag: string, user: string, token: string): string {
    const authStr = `user=${user}\x01auth=Bearer ${token}\x01\x01`;
    const b64 = btoa(authStr);
    return buildCommand(tag, `AUTHENTICATE XOAUTH2 ${b64}`);
}

/** Build LIST command */
export function listCommand(tag: string, ref = '""', pattern = '"*"'): string {
    return buildCommand(tag, `LIST ${ref} ${pattern}`);
}

/** Build SELECT command. Optional `qresync` triggers RFC 7162 fast resync:
 *  the server emits `* VANISHED (EARLIER) <uid-set>` for every UID expunged
 *  since `modSeq` and `* FETCH` for every state change since then, in lieu
 *  of the caller having to diff UID sets. Capability-gated by caller;
 *  requires `ENABLE QRESYNC` already issued on the session. */
export function selectCommand(
    tag: string,
    mailbox: string,
    qresync?: { uidValidity: number; modSeq: number; knownUids?: string },
): string {
    if (!qresync) return buildCommand(tag, `SELECT ${quoteMailbox(mailbox)}`);
    let params = `${qresync.uidValidity} ${qresync.modSeq}`;
    if (qresync.knownUids) params += ` ${qresync.knownUids}`;
    return buildCommand(tag, `SELECT ${quoteMailbox(mailbox)} (QRESYNC (${params}))`);
}

/** Build ENABLE command per RFC 5161. Common extensions to enable: QRESYNC,
 *  CONDSTORE, UTF8=ACCEPT. Must be issued before any SELECT for the
 *  extension to take effect on the session. */
export function enableCommand(tag: string, extensions: string[]): string {
    return buildCommand(tag, `ENABLE ${extensions.join(" ")}`);
}

/** Expand an RFC 3501 UID set (e.g. `"1:5,8,12,20:*"`) into a flat number
 *  list. Does NOT resolve `*` — caller passes a known upper bound only
 *  when it has one; here `*` is treated as a sentinel and skipped so the
 *  call site can decide what to do (typically: ignore — VANISHED never
 *  emits `*` because that would be open-ended). */
export function parseUidSet(set: string, maxExpand = Infinity): number[] {
    // maxExpand caps the expanded output. A VANISHED *range* like
    // `(EARLIER) 1:4828883` (sent when the client's modseq is very stale)
    // would otherwise expand to MILLIONS of integers — burning CPU/memory
    // here and wedging the consumer downstream. The caller passes a cap and
    // treats an at-cap result as "too stale, reconcile some other way"
    // (Bob 2026-06-04). Default Infinity keeps every existing caller unchanged.
    const out: number[] = [];
    for (const part of set.split(",")) {
        if (out.length >= maxExpand) break;
        const trimmed = part.trim();
        if (!trimmed) continue;
        const range = trimmed.split(":");
        if (range.length === 1) {
            const n = parseInt(range[0], 10);
            if (Number.isFinite(n)) out.push(n);
        } else if (range.length === 2) {
            const lo = parseInt(range[0], 10);
            const hi = range[1] === "*" ? lo : parseInt(range[1], 10);
            if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi < lo) continue;
            for (let u = lo; u <= hi && out.length < maxExpand; u++) out.push(u);
        }
    }
    return out;
}

/** Build EXAMINE command (read-only SELECT) */
export function examineCommand(tag: string, mailbox: string): string {
    return buildCommand(tag, `EXAMINE ${quoteMailbox(mailbox)}`);
}

/** Build STATUS command */
export function statusCommand(tag: string, mailbox: string, items: string[] = ["MESSAGES", "UIDNEXT"]): string {
    return buildCommand(tag, `STATUS ${quoteMailbox(mailbox)} (${items.join(" ")})`);
}

/** Build UID FETCH command */
export function fetchCommand(tag: string, range: string, items: string[]): string {
    return buildCommand(tag, `UID FETCH ${range} (${items.join(" ")})`);
}

/** Build sequence-number FETCH command (no UID prefix). Used by fetchLatestN
 *  to avoid SEARCH SINCE on a cold mailbox — sequence ranges are O(1) on the
 *  server because they're just message numbers in the current mailbox. */
export function seqFetchCommand(tag: string, range: string, items: string[]): string {
    return buildCommand(tag, `FETCH ${range} (${items.join(" ")})`);
}

/** Build UID SEARCH command */
export function searchCommand(tag: string, criteria: string): string {
    return buildCommand(tag, `UID SEARCH ${criteria}`);
}

/** Build UID STORE command (set/add/remove flags) */
export function storeCommand(tag: string, uid: number, action: string, flags: string[]): string {
    return buildCommand(tag, `UID STORE ${uid} ${action} (${flags.join(" ")})`);
}

/** Build UID COPY command */
export function copyCommand(tag: string, uid: number, destination: string): string {
    return buildCommand(tag, `UID COPY ${uid} ${quoteMailbox(destination)}`);
}

/** Build UID MOVE command */
export function moveCommand(tag: string, uid: number, destination: string): string {
    return buildCommand(tag, `UID MOVE ${uid} ${quoteMailbox(destination)}`);
}

/** Build APPEND command header (body follows as literal) */
export function appendCommand(tag: string, mailbox: string, flags: string[], size: number): string {
    const flagStr = flags.length > 0 ? ` (${flags.join(" ")})` : "";
    return buildCommand(tag, `APPEND ${quoteMailbox(mailbox)}${flagStr} {${size}}`);
}

/** Build IDLE command */
export function idleCommand(tag: string): string {
    return buildCommand(tag, "IDLE");
}

/** Build NOTIFY SET command (RFC 5465). `spec` is the full event group list,
 *  e.g. "(SELECTED (MessageNew MessageExpunge FlagChange)) (PERSONAL
 *  (MessageNew MessageExpunge FlagChange MailboxName))" — the server then
 *  pushes unsolicited STATUS responses for non-selected mailboxes in the
 *  spec while the connection is idle. Capability gated: only issue when
 *  CAPABILITY response contains NOTIFY. */
export function notifyCommand(tag: string, spec: string): string {
    return buildCommand(tag, `NOTIFY SET ${spec}`);
}

/** Build DONE command (ends IDLE) */
export function doneCommand(): string {
    return "DONE\r\n";
}

/** Build STARTTLS command */
export function starttlsCommand(tag: string): string {
    return buildCommand(tag, "STARTTLS");
}

/** Build LOGOUT command */
export function logoutCommand(tag: string): string {
    return buildCommand(tag, "LOGOUT");
}

/** Build CAPABILITY command */
export function capabilityCommand(tag: string): string {
    return buildCommand(tag, "CAPABILITY");
}

/** Build NOOP command */
export function noopCommand(tag: string): string {
    return buildCommand(tag, "NOOP");
}

/** Build CREATE command */
export function createCommand(tag: string, mailbox: string): string {
    return buildCommand(tag, `CREATE ${quoteMailbox(mailbox)}`);
}

/** Build DELETE command */
export function deleteMailboxCommand(tag: string, mailbox: string): string {
    return buildCommand(tag, `DELETE ${quoteMailbox(mailbox)}`);
}

/** Build RENAME command */
export function renameCommand(tag: string, from: string, to: string): string {
    return buildCommand(tag, `RENAME ${quoteMailbox(from)} ${quoteMailbox(to)}`);
}

// ── Response Parser ──

/** Parse a single IMAP response line */
export function parseResponseLine(line: string): ImapResponse {
    const trimmed = line.replace(/\r?\n$/, "");

    // Continuation response
    if (trimmed.startsWith("+ ") || trimmed === "+") {
        return { tag: "+", type: "CONTINUE", text: trimmed.substring(2), raw: trimmed };
    }

    // Untagged response
    if (trimmed.startsWith("* ")) {
        const rest = trimmed.substring(2);
        // Check for numeric response (e.g., "* 5 EXISTS", "* 131441 FETCH (...)")
        const numMatch = rest.match(/^(\d+)\s+(\S+)/);
        if (numMatch) {
            return { tag: "*", type: numMatch[2].toUpperCase(), text: rest, raw: trimmed };
        }
        // Status/capability/list response
        const spaceIdx = rest.indexOf(" ");
        if (spaceIdx > 0) {
            return { tag: "*", type: rest.substring(0, spaceIdx).toUpperCase(), text: rest.substring(spaceIdx + 1), raw: trimmed };
        }
        return { tag: "*", type: rest.toUpperCase(), text: "", raw: trimmed };
    }

    // Tagged response (e.g., "A1 OK Login completed")
    const spaceIdx = trimmed.indexOf(" ");
    if (spaceIdx > 0) {
        const tag = trimmed.substring(0, spaceIdx);
        const rest = trimmed.substring(spaceIdx + 1);
        const spaceIdx2 = rest.indexOf(" ");
        if (spaceIdx2 > 0) {
            return { tag, type: rest.substring(0, spaceIdx2).toUpperCase(), text: rest.substring(spaceIdx2 + 1), raw: trimmed };
        }
        return { tag, type: rest.toUpperCase(), text: "", raw: trimmed };
    }

    return { tag: "", type: "UNKNOWN", text: trimmed, raw: trimmed };
}

/** Parse a LIST response line: * LIST (\flags) "delimiter" "path" */
export function parseListResponse(text: string): ListData | null {
    // Match: (flags) "delimiter" "path" or (flags) "delimiter" path
    // The "LIST" prefix may or may not be present (parseResponseLine may strip it into r.type)
    const match = text.match(/^(?:LIST\s+)?\(([^)]*)\)\s+"([^"]*)"\s+(?:"([^"]+)"|(\S+))/i);
    if (!match) return null;
    const flags = match[1] ? match[1].split(/\s+/).filter(Boolean) : [];
    const delimiter = match[2] || ".";
    const path = (match[3] || match[4] || "").replace(/\r?\n$/, "");
    return { flags, delimiter, path };
}

/** Parse STATUS response: * STATUS "mailbox" (MESSAGES n UIDNEXT n ...) */
export function parseStatusResponse(text: string): StatusData | null {
    const match = text.match(/\(([^)]+)\)/);
    if (!match) return null;
    const pairs = match[1].split(/\s+/);
    const data: StatusData = {};
    for (let i = 0; i < pairs.length - 1; i += 2) {
        const key = pairs[i].toUpperCase();
        const val = parseInt(pairs[i + 1]);
        if (key === "MESSAGES") data.messages = val;
        else if (key === "RECENT") data.recent = val;
        else if (key === "UIDNEXT") data.uidNext = val;
        else if (key === "UIDVALIDITY") data.uidValidity = val;
        else if (key === "UNSEEN") data.unseen = val;
        // 2026-09-16 — Claude Code (Fable 5.1), at Bob's direction: RFC 8438 SIZE, for imail -sizes.
        else if (key === "SIZE") data.size = val;
    }
    return data;
}

/** Parse a STATUS response keeping the mailbox name. Used by NOTIFY's
 *  unsolicited STATUS pushes where the mailbox identifies which folder
 *  changed — `parseStatusResponse` discards that. Mailbox may be quoted
 *  ("Sent") or atom-shaped (Sent); the regex handles both. */
export function parseStatusResponseFull(text: string): { mailbox: string; data: StatusData } | null {
    // Match: "quoted name" (data)   OR   atomname (data)
    const m = text.match(/^"((?:[^"\\]|\\.)*)"\s*\((.*)\)\s*$/) || text.match(/^(\S+)\s*\((.*)\)\s*$/);
    if (!m) return null;
    const mailbox = m[1].replace(/\\(.)/g, "$1");
    const data: StatusData = {};
    const pairs = m[2].split(/\s+/);
    for (let i = 0; i < pairs.length - 1; i += 2) {
        const key = pairs[i].toUpperCase();
        const val = parseInt(pairs[i + 1]);
        if (key === "MESSAGES") data.messages = val;
        else if (key === "RECENT") data.recent = val;
        else if (key === "UIDNEXT") data.uidNext = val;
        else if (key === "UIDVALIDITY") data.uidValidity = val;
        else if (key === "UNSEEN") data.unseen = val;
        else if (key === "SIZE") data.size = val;   // RFC 8438 (2026-09-16)
    }
    return { mailbox, data };
}

/** Parse UID SEARCH response: * SEARCH 1 2 3 4 5 */
export function parseSearchResponse(text: string): number[] {
    if (!text.trim()) return [];
    return text.trim().split(/\s+/).map(Number).filter(n => !isNaN(n));
}

/** Parse FLAGS from a FETCH or SELECT response */
export function parseFlags(flagStr: string): Set<string> {
    const match = flagStr.match(/\(([^)]*)\)/);
    if (!match) return new Set();
    return new Set(match[1].split(/\s+/).filter(Boolean));
}

/** Parse ENVELOPE from FETCH — simplified parser for the common fields */
export function parseEnvelope(envStr: string): EnvelopeData {
    // ENVELOPE is a parenthesized list — this is a simplified parser
    // Full RFC 3501 envelope: (date subject from sender reply-to to cc bcc in-reply-to message-id)
    const result: EnvelopeData = {
        date: null, subject: "", from: [], sender: [], replyTo: [],
        to: [], cc: [], bcc: [], inReplyTo: "", messageId: ""
    };

    try {
        const tokens = tokenizeParenList(envStr);
        if (tokens.length >= 10) {
            result.date = tokens[0] !== "NIL" ? new Date(unquote(tokens[0])) : null;
            result.subject = decodeImapString(unquote(tokens[1]));
            result.from = parseAddressList(tokens[2]);
            result.sender = parseAddressList(tokens[3]);
            result.replyTo = parseAddressList(tokens[4]);
            result.to = parseAddressList(tokens[5]);
            result.cc = parseAddressList(tokens[6]);
            result.bcc = parseAddressList(tokens[7]);
            // Trim fold whitespace. Servers hand back the ENVELOPE msg-id /
            // in-reply-to with the original header's folding intact when the
            // Message-ID sat on a continuation line ("Message-ID:\r\n\t<...>")
            // — the value arrives as "\t<...>". Stored untrimmed, that tab
            // poisoned every later HEADER search ("missing on server") and
            // fueled mailx's sent-sweep re-APPEND loop: 3,707 duplicates of
            // one Outlook-composed reply (2026-07-02). Trim here so no
            // consumer ever sees the fold whitespace.
            result.inReplyTo = unquote(tokens[8]).trim();
            result.messageId = unquote(tokens[9]).trim();
        }
    } catch {
        // Parsing envelope is best-effort — incomplete data is acceptable
    }
    return result;
}

// ── IMAP String Utilities ──

/** Quote a string for IMAP (handles special characters) */
function quoteString(s: string): string {
    if (/[\x00-\x1f\x7f"\\]/.test(s)) {
        // Use literal for problematic characters
        return `{${new TextEncoder().encode(s).length}}\r\n${s}`;
    }
    return `"${s}"`;
}

/** Quote a mailbox name */
function quoteMailbox(name: string): string {
    if (name === "INBOX") return name;
    if (/[\s"\\*%{]/.test(name)) return `"${name.replace(/["\\]/g, "\\$&")}"`;
    return name;
}

/** Remove quotes from an IMAP string */
function unquote(s: string): string {
    if (!s || s === "NIL") return "";
    if (s.startsWith('"') && s.endsWith('"')) return s.slice(1, -1).replace(/\\(.)/g, "$1");
    return s;
}

/** Build a TextDecoder for an encoded-word charset label, trying progressively
 *  looser readings of the label before giving up on UTF-8.
 *
 *  TextDecoder throws RangeError on a label it doesn't know, and the caller's
 *  catch then returned the RAW encoded text — so ONE unrecognized label put
 *  `London_Startup_Is_Selling_Permanent=2C_Painless=2C_=22Stick-On=22` in the
 *  user's subject line (Bob 2026-08-02, NNSquad via mailman). Labels seen in
 *  the wild that TextDecoder rejects:
 *    `en_US.UTF-8` — a POSIX LOCALE (language_TERRITORY.CODESET), not a
 *                    charset; the real charset is the codeset after the dot.
 *    `utf-8*en`    — RFC 2231 language suffix appended to the charset.
 *    `utf8`        — no hyphen.
 *  The raw label is tried FIRST so legitimate dotted names (`ansi_x3.4-1968`)
 *  are never mangled by the locale rule. Last resort is UTF-8, which decodes
 *  leniently (U+FFFD for bad bytes) rather than throwing — a couple of
 *  replacement characters beat a subject full of quoted-printable. */
function decoderFor(label: string): TextDecoder {
    const raw = label.trim().toLowerCase();
    const candidates = [raw];
    const noLang = raw.split("*")[0];                       // RFC 2231 suffix
    if (noLang !== raw) candidates.push(noLang);
    const locale = noLang.match(/^[a-z]{1,8}(?:_[a-z0-9]{1,8})?\.(.+)$/);
    if (locale) candidates.push(locale[1]);                 // POSIX codeset
    candidates.push(noLang.replace(/^utf[_]?8$/, "utf-8"));
    for (const cs of candidates) {
        try { return new TextDecoder(cs); } catch { /* unknown label — try the next reading */ }
    }
    return new TextDecoder("utf-8");
}

/** Decode IMAP encoded-word (=?charset?encoding?text?=) */
function decodeImapString(s: string): string {
    if (!s) return "";
    // RFC 2047 §6.2: whitespace between adjacent encoded-words must be ignored
    const unfolded = s.replace(/\?=\s+=\?/g, "?==?");
    // Some mailers FOLD INSIDE an encoded-word, stranding the closing `?=` on
    // the next line ("=?utf-8?Q?…_has_Shipped\r\n\t?="). RFC 2047 forbids it,
    // but a JavaMail/hybris sender does it on every order mail in Bob's
    // archive; the unterminated word then matched nothing at all and the raw
    // `=?utf-8?Q?=EF=BB=BFYour_Order_has_Shipped` became the subject line.
    // `[^?]*?` can't cross a `?`, so this only rejoins words whose terminator
    // was separated by folding whitespace — a properly closed encoded-word
    // never has whitespace before its `?=`.
    const rejoined = unfolded.replace(/(=\?[^?]+\?[BQ]\?[^?]*?)\s+\?=/gi, "$1?=");
    const decodedWords = rejoined.replace(/=\?([^?]+)\?([BQ])\?([^?]+)\?=/gi, (_match, charset, encoding, text) => {
        try {
            const decoder = decoderFor(charset);
            if (encoding.toUpperCase() === "B") {
                const raw = atob(text);
                const bytes = new Uint8Array(raw.length);
                for (let j = 0; j < raw.length; j++) bytes[j] = raw.charCodeAt(j);
                return decoder.decode(bytes);
            } else {
                // Quoted-printable: collect bytes then decode with charset
                const decoded = text.replace(/_/g, " ");
                const bytes: number[] = [];
                let i = 0;
                while (i < decoded.length) {
                    if (decoded[i] === "=" && i + 2 < decoded.length) {
                        bytes.push(parseInt(decoded.substring(i + 1, i + 3), 16));
                        i += 3;
                    } else {
                        bytes.push(decoded.charCodeAt(i));
                        i++;
                    }
                }
                return decoder.decode(new Uint8Array(bytes));
            }
        } catch {
            // Only reachable now for malformed base64 (atob throws). Strip the
            // Q-encoding's underscore-for-space at least, so the fallback isn't
            // more wrong than it has to be.
            return encoding.toUpperCase() === "Q" ? text.replace(/_/g, " ") : text;
        }
    });
    // A BOM inside the encoded-word (`=EF=BB=BF…`) is an encoding marker the
    // sender leaked into the text, not content — invisible, but it still sorts,
    // searches and truncates as a character. Drop it.
    return decodedWords.replace(/\uFEFF/g, "");   // U+FEFF
}

/** Tokenize a parenthesized IMAP list (top-level only) */
function tokenizeParenList(s: string): string[] {
    const tokens: string[] = [];
    let i = 0;
    const str = s.trim();
    // Skip outer parens
    const start = str.startsWith("(") ? 1 : 0;
    const end = str.endsWith(")") ? str.length - 1 : str.length;

    i = start;
    while (i < end) {
        // Skip whitespace
        while (i < end && str[i] === " ") i++;
        if (i >= end) break;

        if (str[i] === "(") {
            // Nested paren group — find matching close
            let depth = 1;
            let j = i + 1;
            while (j < end && depth > 0) {
                if (str[j] === "(") depth++;
                else if (str[j] === ")") depth--;
                j++;
            }
            tokens.push(str.substring(i, j));
            i = j;
        } else if (str[i] === '"') {
            // Quoted string
            let j = i + 1;
            while (j < end) {
                if (str[j] === "\\" && j + 1 < end) { j += 2; continue; }
                if (str[j] === '"') { j++; break; }
                j++;
            }
            tokens.push(str.substring(i, j));
            i = j;
        } else if (str.substring(i, i + 3).toUpperCase() === "NIL") {
            tokens.push("NIL");
            i += 3;
        } else {
            // Atom
            let j = i;
            while (j < end && str[j] !== " " && str[j] !== ")" && str[j] !== "(") j++;
            if (j === i) {
                // str[i] is a stray ')' (or '(') that none of the branches
                // above consumed — an unbalanced/malformed parenthesised
                // list (Dovecot mis-parses some Outlook `From:` headers and
                // emits exactly this). Without advancing, `i` is stuck and
                // the while-loop spins FOREVER, pinning a CPU core and
                // wedging the whole daemon event loop. Skip the stray char.
                i++;
                continue;
            }
            tokens.push(str.substring(i, j));
            i = j;
        }
    }
    return tokens;
}

/** Parse an IMAP address list: ((name NIL mailbox host) ...) */
function parseAddressList(token: string): AddressData[] {
    if (!token || token === "NIL") return [];
    const addrs: AddressData[] = [];
    // Tokenize the outer list to get each address group as a paren-delimited token
    // e.g. ((name NIL mailbox host)(name2 NIL mailbox2 host2)) → ["(name NIL mailbox host)", "(name2 ...)"]
    const groups = tokenizeParenList(token);
    for (const group of groups) {
        if (!group.startsWith("(")) continue;
        const parts = tokenizeParenList(group);
        if (parts.length >= 4) {
            const name = decodeImapString(unquote(parts[0]));
            let mailbox = unquote(parts[2]);
            let host = unquote(parts[3]);
            // Dovecot stamps "MISSING_MAILBOX" / "MISSING_DOMAIN" into the
            // ENVELOPE tuple when the source From: header didn't parse cleanly
            // (RFC 3501 forbids NIL in those slots when any address part is
            // present). Strip the sentinels so we don't render
            // "user@iecc.com@MISSING_DOMAIN" — observed in the wild from
            // Outlook's malformed `From: "name" <"local@domain">` over-quoting.
            if (host === "MISSING_DOMAIN") host = "";
            if (mailbox === "MISSING_MAILBOX") mailbox = "";
            // Recover from Outlook's quoted-string pathology: when the local
            // part itself contains an "@" the entire mailbox value is already
            // a full address, so don't append @host again. Same hand applies
            // to any server that mis-parses an over-quoted From header.
            const address = mailbox.includes("@")
                ? mailbox
                : (mailbox && host ? `${mailbox}@${host}` : mailbox || "");
            addrs.push({ name, address });
        }
    }
    return addrs;
}

/** Build SEARCH criteria string from common search parameters */
export function buildSearchCriteria(criteria: {
    since?: Date;
    before?: Date;
    from?: string;
    to?: string;
    subject?: string;
    body?: string;
    uid?: string;  // UID range like "100:*"
    all?: boolean;
}): string {
    const parts: string[] = [];
    if (criteria.all) parts.push("ALL");
    if (criteria.since) parts.push(`SINCE ${formatImapDate(criteria.since)}`);
    if (criteria.before) parts.push(`BEFORE ${formatImapDate(criteria.before)}`);
    if (criteria.from) parts.push(`FROM "${criteria.from}"`);
    if (criteria.to) parts.push(`TO "${criteria.to}"`);
    if (criteria.subject) parts.push(`SUBJECT "${criteria.subject}"`);
    if (criteria.body) parts.push(`BODY "${criteria.body}"`);
    if (criteria.uid) parts.push(`UID ${criteria.uid}`);
    return parts.length > 0 ? parts.join(" ") : "ALL";
}

/** Format a date for IMAP (DD-Mon-YYYY) */
function formatImapDate(d: Date): string {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${d.getDate()}-${months[d.getMonth()]}-${d.getFullYear()}`;
}
